import { NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  Inject,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';

import { Store } from '@ngrx/store';

import { CaNumber } from '../../../../../../@store/products/ca-numbers/ca-numbers.model';
import { QuotesActions } from '../../../../../../@store/quotes/quotes.actions';
import {
  selectCANumbers,
  selectCANumbersLoading,
  selectSelectedCANumber,
  selectSelectedCANumberError,
  selectSelectedCANumberSuccess,
} from '../../../../../../@store/quotes/quotes.selectors';
import { CONFIG } from '../../../../../../config';

@Component({
  selector: 'ciwc-order-add-to-ca',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatProgressSpinner,
    NgIf,
    NgFor,
    MatAutocompleteModule,
    MatInputModule,
  ],
  templateUrl: './order-add-to-ca.component.html',
  styleUrl: './order-add-to-ca.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderAddToCaComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private store = inject(Store);
  private fb = inject(FormBuilder);
  private selectedCaId: number | null = null;

  readonly caNumbers = this.store.selectSignal(selectCANumbers);
  readonly caNumbersLoading = this.store.selectSignal(selectCANumbersLoading);
  readonly selectedCANumber = this.store.selectSignal(selectSelectedCANumber);
  readonly selectedCaNumbersError = this.store.selectSignal(selectSelectedCANumberError);
  readonly selectedCaNumbersSuccess = this.store.selectSignal(selectSelectedCANumberSuccess);

  form: FormGroup;
  submitted = signal(false);
  filteredCaNumbers: CaNumber[] = [];
  allCaNumbers: CaNumber[] = [];
  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: Record<string, unknown>,
    private dialogRef: MatDialogRef<OrderAddToCaComponent>,
  ) {
    this.form = this.fb.group({
      caNumber: [null],
    });

    effect(() => {
      if (this.caNumbers()) {
        this.form.get('caNumber')?.enable();
        this.filteredCaNumbers = this.caNumbers() || [];
      } else {
        this.form.get('caNumber')?.disable();
        this.filteredCaNumbers = [];
      }

      if (this.selectedCaNumbersError()) {
        this.submitted.set(false);
      }

      if (this.selectedCaNumbersSuccess() && this.selectedCANumber()) {
        this.dialogRef.close({
          id: this.selectedCaId,
          details: this.selectedCANumber(),
        });

        this.store.dispatch(QuotesActions.clearSelectedCANumber());
      }
    });
  }

  ngOnInit() {
    this.store.dispatch(
      QuotesActions.loadCANumbers({
        payload: {
          limit: -1,
        },
      }),
    );
  }

  onCaNumberInput(value: string) {
    if (!value) {
      this.filteredCaNumbers = this.caNumbers() || [];

      return;
    }
    const filterValue = value.toLowerCase();
    this.filteredCaNumbers =
      this.caNumbers()?.filter((ca) => ca.caNumber.toLowerCase().includes(filterValue)) || [];
  }

  displayCaNumber = (caNumber: CaNumber): string => {
    return caNumber ? caNumber.caNumber : '';
  };

  onCaNumberFocus(value: string) {
    if (!value) {
      this.filteredCaNumbers = this.caNumbers() || [];
    }
  }

  onCaNumberSelected(event: MatAutocompleteSelectedEvent) {
    const caNumber = event.option.value as CaNumber;
    this.selectedCaId = caNumber.id;
  }

  onSelectionChange(event: { value: number }) {
    this.selectedCaId = event.value;
  }

  onSubmit() {
    this.submitted.set(true);
    if (this.form.invalid || !this.selectedCaId) return;

    // Dispatch action to load CA number details
    this.store.dispatch(
      QuotesActions.loadCANumberById({ id: `${this.selectedCaId}`, includeRelations: false }),
    );
  }

  onCancel() {
    this.dialogRef.close();
  }

  trackById(index: number, item: CaNumber) {
    return item.id;
  }
}
